import React, { useState, useRef, useCallback } from 'react';
import { FiX, FiImage, FiVideo, FiUpload } from 'react-icons/fi';
import { postsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function CreatePostModal({ onClose, onCreated }) {
  const { user }              = useAuth();
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef               = useRef();

  const handleFile = f => {
    if (!f) return;
    const allowed = ['image/jpeg','image/png','image/gif','image/webp','video/mp4','video/quicktime'];
    if (!allowed.includes(f.type)) return toast.error('Unsupported file type.');
    if (f.size > 50 * 1024 * 1024) return toast.error('File must be under 50MB.');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  }, []);

  const handleSubmit = async () => {
    if (!file) return toast.error('Please select a photo or video.');
    setLoading(true);
    const fd = new FormData();
    fd.append('media', file);
    fd.append('caption', caption.trim());
    try {
      const res = await postsAPI.createPost(fd);
      toast.success('Post shared! 🎉');
      onCreated?.(res.data.post_id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create post.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box card fade-in" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{fontSize:16,fontWeight:700}}>Create New Post</h2>
          <button className="btn btn-icon btn-ghost" onClick={onClose}><FiX size={20}/></button>
        </div>
        <div className="modal-body">
          <div className="flex-c gap-1" style={{marginBottom:16}}>
            {user?.avatar
              ? <img src={user.avatar} className="avatar" style={{width:36,height:36}} alt="" />
              : <div style={{width:36,height:36,borderRadius:'50%',background:'linear-gradient(135deg,#E91E8C,#7B2FF7)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700}}>
                  {user?.full_name?.[0]}
                </div>
            }
            <div>
              <p className="font-bold" style={{fontSize:14}}>{user?.full_name}</p>
              <p className="text-3 text-xs">@{user?.username}</p>
            </div>
          </div>
          <textarea className="input" placeholder="Write a caption..." value={caption}
            onChange={e => setCaption(e.target.value)} rows={3}
            style={{resize:'none',marginBottom:4,lineHeight:1.6}} maxLength={2200} />
          <p className="text-3 text-xs" style={{marginBottom:12,textAlign:'right'}}>{caption.length}/2200</p>
          {!preview ? (
            <div
              className={"drop-zone" + (dragging ? " dragging" : "")}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
            >
              <div style={{display:'flex',gap:12,justifyContent:'center',marginBottom:12}}>
                <FiImage size={32} style={{color:'#E91E8C'}} />
                <FiVideo size={32} style={{color:'#7B2FF7'}} />
              </div>
              <p className="font-bold" style={{marginBottom:6}}>Drag & drop or click to upload</p>
              <p className="text-3 text-sm">JPEG, PNG, GIF, WebP, MP4 — max 50MB</p>
              <input ref={fileRef} type="file" accept="image/*,video/*" hidden
                onChange={e => handleFile(e.target.files?.[0])} />
            </div>
          ) : (
            <div style={{position:'relative',borderRadius:'12px',overflow:'hidden',marginBottom:4}}>
              {file?.type?.startsWith('video')
                ? <video src={preview} controls style={{width:'100%',maxHeight:320,objectFit:'cover',display:'block'}} />
                : <img src={preview} alt="Preview" style={{width:'100%',maxHeight:320,objectFit:'cover',display:'block'}} />
              }
              <button onClick={() => { setFile(null); setPreview(null); }}
                style={{position:'absolute',top:8,right:8,background:'rgba(0,0,0,.55)',color:'#fff',border:'none',borderRadius:'50%',width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
                <FiX size={14}/>
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={loading || !file}>
            {loading ? 'Sharing…' : 'Share Post'}
          </button>
        </div>
      </div>
      <style>{`
        .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:500;display:flex;align-items:center;justify-content:center;padding:16px}
        .modal-box{width:100%;max-width:520px;max-height:90vh;overflow-y:auto}
        .modal-header{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid var(--border)}
        .modal-body{padding:20px}
        .modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:14px 20px;border-top:1px solid var(--border)}
        .drop-zone{border:2px dashed var(--border);border-radius:12px;padding:40px 20px;text-align:center;cursor:pointer;transition:all .2s}
        .drop-zone:hover,.drop-zone.dragging{border-color:#E91E8C;background:rgba(233,30,140,.04)}
      `}</style>
    </div>
  );
}
